import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DashboardQueryKey } from '../constants/dashboardQueryKeys';
import { notificationSettingsService } from '../services/notificationSettings/NotificationSettingsService';
import i18n from '../config/i18n/i18n';

const notificationSettingsKey = [DashboardQueryKey.NOTIFICATION_SETTINGS] as const;

export function useNotificationSettingsQuery() {
  return useQuery({
    queryKey: notificationSettingsKey,
    queryFn: () => notificationSettingsService.getSettings(),
    // Linking happens out-of-band in the Telegram bot, so window focus isn't a
    // reliable signal (in-app browsers often don't fire it). Poll while still
    // unlinked and stop once linked, so the "connect" banner auto-dismisses
    // shortly after the user links.
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data?.telegramLinked ? false : 10_000,
  });
}

export function useConnectTelegramMutation() {
  return useMutation({
    // Pass the current app locale so Telegram notifications come in the user's
    // app language, not their Telegram client UI locale.
    mutationFn: () => notificationSettingsService.connectTelegram(i18n.language),
  });
}

export function useDisconnectTelegramMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationSettingsService.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationSettingsKey });
    },
  });
}
